import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, Info, XCircle } from "lucide-react";
import type { DashboardReport } from "@/api/reports";
import { accrualText, dueSoonText } from "@/lib/compliance";
import { formatDate, formatInr } from "@/lib/format";
import { formatTotals } from "@/lib/money";
import { cn } from "@/lib/utils";

export type AttentionTone = "critical" | "warning" | "info";

export type AttentionItem = {
  key: string;
  tone: AttentionTone;
  title: string;
  detail?: string;
  to: string;
};

const ORDER: Record<AttentionTone, number> = { critical: 0, warning: 1, info: 2 };
const STALE_MAIL_MS = 3_600_000;

export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

/** "2 renewals due within 14 days, 1 overdue" - whichever parts are not zero. */
function renewalsText(due: number, overdue: number): string {
  const parts = [];
  if (due > 0) parts.push(`${plural(due, "renewal")} due within 14 days`);
  if (overdue > 0) parts.push(`${due > 0 ? overdue : plural(overdue, "renewal")} overdue`);
  return parts.join(", ");
}

/** A configured mailbox that errored, or has not polled for an hour, needs a look. */
function mailIsStale(mail: DashboardReport["mail"], now: number): boolean {
  if (!mail.configured) return false;
  if (mail.last_error) return true;
  return mail.last_polled_at !== null && now - new Date(mail.last_polled_at).getTime() > STALE_MAIL_MS;
}

/** Everything on the dashboard that wants a decision, the most urgent first. */
export function attentionItems(data: DashboardReport, now = Date.now()): AttentionItem[] {
  const items: AttentionItem[] = [];
  const { compliance, bills, gst } = data;
  const next = compliance.next_due;

  if (data.overdue_invoices > 0) {
    items.push({
      key: "overdue",
      tone: "critical",
      title: `${plural(data.overdue_invoices, "invoice")} overdue`,
      detail: `${formatTotals(data.overdue)} past the due date`,
      to: "/invoices?status=overdue",
    });
  }
  if (compliance.overdue > 0) {
    items.push({
      key: "compliance-overdue",
      tone: "critical",
      title: `${plural(compliance.overdue, "filing")} overdue`,
      detail: accrualText(compliance),
      to: "/compliance",
    });
  }
  if (gst.level !== "ok") {
    items.push({
      key: "gst",
      tone: gst.level === "exceeded" ? "critical" : "warning",
      title:
        gst.level === "exceeded"
          ? "Turnover has passed the GST threshold"
          : `Turnover is at ${gst.percentage}% of the GST threshold`,
      detail: "Talk to your CA about registering.",
      to: "/reports?report=gst",
    });
  }
  if (data.drafts > 0) {
    items.push({
      key: "drafts",
      tone: "warning",
      title: `${plural(data.drafts, "draft invoice")} to issue`,
      detail: "Preview each one, then issue it to give it a number.",
      to: "/invoices?status=draft",
    });
  }
  if (next && next.days <= 30) {
    items.push({
      key: "compliance-due",
      tone: "warning",
      title: `${next.form} ${next.period_label} due ${formatDate(next.due_on)}`,
      detail: dueSoonText(next, compliance.due_soon),
      to: "/compliance",
    });
  }
  if (data.profit.not_converted.length > 0) {
    items.push({
      key: "rate",
      tone: "warning",
      title: `${formatTotals(data.profit.not_converted)} waiting for a rupee figure`,
      detail: "Add the INR credited, or the rate, on the payment so it counts as income.",
      to: "/payments",
    });
  }
  if (bills.unpaid > 0) {
    items.push({
      key: "bills",
      tone: "warning",
      title: `${plural(bills.unpaid, "bill")} unpaid`,
      detail: `${formatInr(bills.unpaid_inr)} committed; mark each one paid when it goes out.`,
      to: "/expenses?status=unpaid",
    });
  }
  if (data.budget_over.length > 0) {
    items.push({
      key: "budget-over",
      tone: "warning",
      title: `Over budget this month: ${data.budget_over.join(", ")}`,
      to: "/money?tab=budget",
    });
  }
  if (data.budget_fund_short.length > 0) {
    items.push({
      key: "fund-short",
      tone: "warning",
      title: `Spending ahead of what clients have paid: ${data.budget_fund_short.join(", ")}`,
      detail: "These buckets have spent more than their share of the money received so far.",
      to: "/money?tab=budget",
    });
  }
  if (mailIsStale(data.mail, now)) {
    items.push({
      key: "mail",
      tone: "warning",
      title: "The mailbox has not synced for over an hour",
      detail: data.mail.last_error || "Check the mail service on the server.",
      to: "/mail",
    });
  }
  if (bills.renewals_due + bills.renewals_overdue > 0) {
    items.push({
      key: "renewals",
      tone: bills.renewals_overdue > 0 ? "warning" : "info",
      title: renewalsText(bills.renewals_due, bills.renewals_overdue),
      to: "/expenses?tab=bills",
    });
  }
  if (bills.to_reimburse > 0) {
    items.push({
      key: "reimburse",
      tone: "info",
      title: `${formatInr(bills.to_reimburse_inr)} to reimburse`,
      detail: `${plural(bills.to_reimburse, "bill")} paid from someone's pocket.`,
      to: "/money?tab=owed",
    });
  }
  if (data.pending_transfers > 0) {
    items.push({
      key: "transfers",
      tone: "info",
      title: `${plural(data.pending_transfers, "transfer")} still pending`,
      detail: "The money moves only once a transfer is marked completed.",
      to: "/money?tab=transfers",
    });
  }
  if (data.awaiting_bank_letter > 0) {
    items.push({
      key: "bank-letter",
      tone: "info",
      title: `${plural(data.awaiting_bank_letter, "invoice")} waiting for the bank letter`,
      detail: "Not due until the client has the Director-signed letter; record its date in Settings.",
      to: "/settings?tab=banks",
    });
  }
  return items.sort((a, b) => ORDER[a.tone] - ORDER[b.tone]);
}

const TONE = {
  critical: { icon: XCircle, className: "text-destructive", label: "Urgent" },
  warning: { icon: AlertTriangle, className: "text-warning-foreground", label: "Needs action" },
  info: { icon: Info, className: "text-brand-600", label: "For your information" },
} as const;

export function AttentionList({ items }: { items: AttentionItem[] }) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <CheckCircle2 className="h-8 w-8 text-success" aria-hidden />
        <p className="font-display text-lg font-semibold">All clear</p>
        <p className="max-w-xs text-sm text-muted-foreground">
          Overdue invoices, drafts to issue and filings coming due will show up here.
        </p>
      </div>
    );
  }
  return (
    <ul className="divide-y">
      {items.map((item) => {
        const tone = TONE[item.tone];
        return (
          <li key={item.key}>
            <Link
              to={item.to}
              className="group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none md:px-5"
            >
              <tone.icon className={cn("mt-0.5 h-4 w-4 shrink-0", tone.className)} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="sr-only">{tone.label}: </span>
                <span className="block text-sm font-semibold text-foreground">{item.title}</span>
                {item.detail && (
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                    {item.detail}
                  </span>
                )}
              </span>
              <ChevronRight
                className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                aria-hidden
              />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
